import { useMemo } from 'react';
import { ExtendedTractor } from '@hooks/useDriverTractors.types';
import { DataGridPro, GridRowClassNameParams, GridRowId, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { getTractorColumnDefinitions, getTractorMobileColumnDefinitions } from './columns';
import AssignDriverGridToolbar from '../AssignDriverToolbar';
import { useMediaQuery, useTheme } from '@mui/material';
import { isTractorSelectable } from './types';
import { applyThemeGridSxOverrides } from '@/Infrastructure/Theme/AppThemeOverrides';
import { GenericOverlay } from '../GenericNoRowsOverlay';
import { TractorDataGridProps } from '../types';

import styles from './DataGrid.Tractor.module.css';

export default function Grid({ legRequiresHazmat, ...props }: TractorDataGridProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	function onSingleRowSelect(rowSelectionModel: GridRowSelectionModel) {
		if (rowSelectionModel.ids.size > 0) {
			const [, selectedRowId] = rowSelectionModel.ids.entries().next().value as [never, GridRowId];

			const selectedRow = props.rows.find((t) => t.id === selectedRowId);

			if (selectedRow) {
				/** Execute the callback */
				props.onRowSelect(selectedRow);
			}
		} else {
			props.onRowSelect(null);
		}
	}

	function isRowSelectable({ row }: GridRowParams<ExtendedTractor>) {
		return isTractorSelectable(row, legRequiresHazmat).isSelectable;
	}

	function getRowCssClasses({ row }: GridRowClassNameParams<ExtendedTractor>) {
		let cssClasses = '';

		if (!isTractorSelectable(row, legRequiresHazmat).isSelectable) {
			cssClasses = styles['disabled-row'];
		}

		if (row.id === props.defaultTractorId) {
			cssClasses = `${cssClasses} ${styles['default-row']}`.trimStart();
		}

		return cssClasses;
	}

	const tractorColumns = useMemo(() => getTractorColumnDefinitions(legRequiresHazmat ?? false), [legRequiresHazmat]);

	const mobileTractorColumns = useMemo(() => getTractorMobileColumnDefinitions(legRequiresHazmat ?? false), [legRequiresHazmat]);

	return (
		<DataGridPro
			apiRef={props.gridApiRef}
			rows={props.rows}
			columns={isMobile ? mobileTractorColumns : tractorColumns}
			onRowSelectionModelChange={onSingleRowSelect}
			isRowSelectable={isRowSelectable}
			getRowClassName={getRowCssClasses}
			rowHeight={isMobile ? 75 : 50}
			columnHeaderHeight={35}
			slots={{
				toolbar: AssignDriverGridToolbar,
				noRowsOverlay: () => <GenericOverlay overlayText='No Trucks Found' />,
				noResultsOverlay: () => <GenericOverlay overlayText='No Truck Results' />
			}}
			slotProps={{
				toolbar: {
					driverToolbarOverrides: {
						slickRef: props.slickRef,
						defaultFilterModel: props.defaultFilterModel
					}
				}
			}}
			columnVisibilityModel={{
				drvId: props.displayCurrentDriver ?? false,
				hazmatExpirationDate: false,
				isAssignedJob: false,
				isOnLastJob: false
			}}
			sx={{
				...applyThemeGridSxOverrides(),
				'& .MuiDataGrid-columnHeaders': isMobile
					? {
							display: 'none'
						}
					: {}
			}}
		/>
	);
}
